import { WrapperUIRoot } from "./WrapperUI.styles";

import React, { useEffect, useRef, useState } from "react";
import actionsList from "../../app/actions-list";

import { CustomUI } from "../CustomUI";

import {
  // ArcwarePixelStreaming,
  // ArcwareApplication,
  ArcwareInit,
} from "@arcware-cloud/pixelstreaming-websdk";
import { DialogGeneric } from "../DialogGeneric";
import { StartScreen } from "../StartScreen";
import { WelcomeAndIntructionsPopUp } from "../WelcomeAndIntructionsPopUp";
import { useSelectedOptions } from "../../context/SelectedOptionsContext";
import { PopUpSummary } from "../PopUpSummary";
import { Loader } from "../Loader";
import { FullScreenButton } from "../FullScreenButton";
import { DynamicViewportOverlay } from "../DynamicViewportOverlay";
import { useDevice } from "../../hooks/useDevice";

const CONTAINER_ID = "shared-stream-container";

export const WrapperUI = () => {
  const {
    summaryOpen,
    closeSummary,
    showDynamicViewportOverlay,
    currentResolution,
  } = useSelectedOptions();
  const ref = useRef(null);
  const { isIOS } = useDevice();

  const [showInstructions, setShowInstructions] = useState(false);

  const [videoInitialized, setVideoInitialized] = useState(false);
  const [applicationResponse, setApplicationResponse] = useState();
  const [arcwareApplication, setArcwareApplication] = useState();
  const [showStartScreen, setShowStartScreen] = useState(true);

  const [showExportLoader, setShowExportLoader] = useState(false);
  const [exportProgress, setExportProgress] = useState(0);

  const [showBgLoader, setShowBgLoader] = useState(false);

  const exportIntervalRef = React.useRef(null);
  const finishTimeoutRef = React.useRef(null);
  const downloadTimeoutRef = React.useRef(null);

  // Clear everything safely
  function resetExportTimers() {
    if (exportIntervalRef.current) clearInterval(exportIntervalRef.current);
    if (finishTimeoutRef.current) clearTimeout(finishTimeoutRef.current);
    if (downloadTimeoutRef.current) clearTimeout(downloadTimeoutRef.current);
    exportIntervalRef.current =
      finishTimeoutRef.current =
      downloadTimeoutRef.current =
        null;
  }
  // ---- PROGRESS API (no useCallback)
  function startExportProgress() {
    resetExportTimers();

    setShowExportLoader(true);
    setExportProgress(0);

    exportIntervalRef.current = setInterval(() => {
      setExportProgress((prev) => (prev >= 100 ? 100 : prev + 10));
    }, 1500);
  }

  function stopExportInterval() {
    if (exportIntervalRef.current) {
      clearInterval(exportIntervalRef.current);
      exportIntervalRef.current = null;
    }
  }

  function createFile(file) {
    try {
      // finish progress
      if (exportIntervalRef.current) clearInterval(exportIntervalRef.current);
      exportIntervalRef.current = null;
      setExportProgress(100);

      // close loader after 600ms
      finishTimeoutRef.current = setTimeout(() => {
        setShowExportLoader(false);

        // trigger download shortly after
        downloadTimeoutRef.current = setTimeout(() => {
          const blob = new Blob(file.data, { type: file.mimetype });
          const url = URL.createObjectURL(blob);
          const a = document.createElement("a");

          const now = new Date();
          const timestamp =
            String(now.getDate()).padStart(2, "0") +
            String(now.getMonth() + 1).padStart(2, "0") +
            now.getFullYear() +
            "-" +
            String(now.getHours()).padStart(2, "0") +
            String(now.getMinutes()).padStart(2, "0") +
            String(now.getSeconds()).padStart(2, "0");

          a.href = url;
          a.download = `wydst_configuration_export-${currentResolution}-${timestamp}${file.extension}`;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          URL.revokeObjectURL(url);
        }, 250);
      }, 600);
    } finally {
      // schedule a final cleanup just in case
      setTimeout(resetExportTimers, 2000);
    }
  }
  const handleDialogShow = (value, reason) => {
    if (reason === "backdropClick") return;
    setShowStartScreen(value);
    const instructionsRead = localStorage.getItem("showInstructions");
    if (instructionsRead !== "false") setShowInstructions(!value);
  };

  const handleInstructionsShow = (value, reason) => {
    if (reason === "backdropClick") return;
    setShowInstructions(value);
    localStorage.setItem("showInstructions", "false");
  };

  useEffect(() => {
    if (ref.current) return;

    const { PixelStreaming, Application } = ArcwareInit(
      {
        shareId: "share-3b65524b-46c3-4651-b659-525cc98b00d7",
      },
      {
        initialSettings: {
          AutoConnect: true,
          TouchInput: true,
          customUI: true,
        },
        settings: {
          audioButton: false,
          fullscreenButton: false,
          micButton: false,
          settingsButton: false,
          infoButton: false,
          connectionStrengthIcon: false,
          stopButton: false, //remove later
        },
      }
    );

    if (window.location.hostname === "localhost") {
      window.arc = {
        app: Application,
        aps: PixelStreaming,
      };
    }

    let timerId = null; // Use this if you're outside a React component/hook

    setArcwareApplication(Application);
    Application.getApplicationResponse((response) => {
      setApplicationResponse(response);
      window.file = Application.webRtcController.file;
      if (response.startsWith("CreateScreenshot")) createFile(window.file);

      if (response === `{ "Loading": "Start" }`) setShowBgLoader(true);
      if (response === `{ "Loading": "Finish" }`) {
        // 2. Clear any existing timer before setting a new one (safety)
        if (timerId) {
          clearTimeout(timerId);
        }

        // 3. Set the delayed call and store the ID
        timerId = setTimeout(() => {
          setShowBgLoader(false);
          timerId = null; // Clear the ID after execution
        }, 1000); // 1 second delay
      }

      return () => {
        stopExportInterval();
      };
    });

    PixelStreaming.videoInitializedHandler.add(() => setVideoInitialized(true));

    window.application = Application; // Dev-only
    ref.current = document.querySelector(`#${CONTAINER_ID}`);
    if (!ref.current) throw new Error("Ref.current is undefined.");
    ref.current.appendChild(Application.rootElement);
  }, []);

  useEffect(() => {
    return () => {
      resetExportTimers();
      if (videoInitialized && arcwareApplication) {
        arcwareApplication.stream.removePlayer();
      }
    };
  }, [arcwareApplication, videoInitialized]);

  useEffect(() => {
    if (videoInitialized);
  }, [videoInitialized]);

  useEffect(() => {
    // if (applicationResponse === '{ "loading_screen": "disabled" }') {
    //   setTimeout(() => {
    //     setOpacityLoader(false);
    //   }, 2000);
    // }
  }, [applicationResponse]);

  return (
    <>
      <WrapperUIRoot
        id={CONTAINER_ID}
        sx={{
          width: "100%",
          height: "100%",
          position: "relative",
          opacity: showStartScreen ? 0 : 1,
        }}
      >
        {videoInitialized && (
          <>
            {!isIOS && <FullScreenButton />}
            <CustomUI
              emitUIInteraction={arcwareApplication?.emitUIInteraction}
              applicationResponse={applicationResponse}
              actionsList={actionsList}
              loading={false}
              showingOverlay={showStartScreen}
              onHandleStartExport={startExportProgress}
              videoInitialized={videoInitialized}
            />
          </>
        )}
      </WrapperUIRoot>
      <DialogGeneric
        show={showStartScreen}
        onHandleClose={handleDialogShow}
        maxWidth="lg"
        sx={{
          zIndex: 99999,
          ".MuiDialog-paper": { overflow: "hidden" },
        }}
      >
        <StartScreen
          onHandleClose={handleDialogShow}
          videoInitialized={videoInitialized}
          arcwareApplication={arcwareApplication}
          environmentLoading={showBgLoader}
        />
      </DialogGeneric>
      <DialogGeneric
        show={showInstructions}
        onHandleClose={handleInstructionsShow}
        maxWidth="md"
        sx={{
          zIndex: 9999,
          ".MuiDialog-paper": { overflow: "hidden" },
        }}
      >
        <WelcomeAndIntructionsPopUp />
      </DialogGeneric>
      <DialogGeneric
        show={summaryOpen}
        onHandleClose={(value, reason) => {
          if (reason === "backdropClick") return;
          if (!value) closeSummary();
        }}
        maxWidth="md"
        sx={{
          zIndex: 9999,
          ".MuiDialog-paper": { overflow: "hidden" },
        }}
      >
        <PopUpSummary />
      </DialogGeneric>
      <Loader
        open={showExportLoader}
        value={exportProgress}
        progress={true}
        blurValue={2}
        bgOpacity={0.6}
        label="Exporting your image"
      />

      <Loader
        open={showBgLoader}
        progress={false}
        blurValue={40}
        bgOpacity={0.75}
        label="Loading environment"
      />

      <DynamicViewportOverlay open={showDynamicViewportOverlay} />
    </>
  );
};
